import React from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { graphql, Link } from 'gatsby';

import { useGetStartedSectionData } from './hooks';

import HorizontalArrow from '../assets/horizontalArrow.inline.svg';

import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';

export type TGetStartedSection = Normalize<Queries.HomeContentGetStartedDataFragment>;

const Container = styled.div`
  ${({ theme: { colors, mq, spacings } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colors.backgroundDark};
    padding: 23rem 0 19.75rem;

    ${mq.md} {
      padding: 5rem ${spacings.padding} 8rem;
    }
  `}
`;

const TabContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    row-gap: 9rem;
    align-items: center;
    border-radius: 2rem;
    background-color: #343434;
    padding: 8rem 0;
    min-width: 138.25rem;

    ${mq.md} {
      row-gap: 4rem;
      padding: 4rem 1.75rem;
      width: 100%;
      min-width: initial;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h3 {
      margin: 0;
      text-align: center;
      line-height: 0.965;
      color: ${colors.white};
      font-size: 5.5rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }

    ${mq.md} {
      & > h3 {
        font-size: 3rem;
      }
    }
  `}
`;

const arrowAnimation = keyframes`
  0% {
    transform: translateX(0);  
  }
  
  100% {
    transform: translateX(1.25rem) ;
  }

`;

const Button = styled(Link)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    column-gap: 1rem;
    align-items: center;
    align-self: center;
    justify-self: center;
    border-radius: 2rem;
    background-color: ${colors.primary};
    padding: 2.1875rem 4.6875rem;
    text-decoration: none;
    line-height: 1.39;
    color: ${colors.black};
    font-size: 2.25rem;

    ${tp.useFont(fonts => fonts['Nunito Sans'].bold)};

    & > svg {
      width: 4rem;
      height: 4rem;
      animation: ${arrowAnimation} 2s ease-in-out infinite alternate;
    }

    ${mq.md} {
      justify-content: center;
      justify-self: stretch;
    }
  `}
`;

const GetStartedSection: React.FC<TGetStartedSection> = sliceData => {
  const { title, link, linkLabel } = useGetStartedSectionData(sliceData);

  return (
    <Container>
      <TabContainer>
        <Title render={title} />
        <Button to={link}>
          {linkLabel}
          <HorizontalArrow />
        </Button>
      </TabContainer>
    </Container>
  );
};

export const contentGetStartedFragment = graphql`
  fragment HomeContentGetStartedData on PrismicHomeDataContentHomegetstarted {
    slice_type
    primary {
      home_get_started_title {
        richText
      }
      home_get_started_link_label
      home_get_started_link {
        url
      }
    }
  }
`;

export default GetStartedSection;
